import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Typography, FormHelperText, Tooltip } from '@mui/material';

interface MonthDayInputProps {
  selectedDays: number[];
  onChange: (days: number[]) => void;
}

const MonthDayInput: React.FC<MonthDayInputProps> = ({
  selectedDays,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState(selectedDays.length > 0 ? selectedDays.join(', ') : '');
  const [error, setError] = useState('');
  const isTypingRef = useRef(false);

  // Update input value when selectedDays changes externally (not from user typing)
  useEffect(() => {
    if (!isTypingRef.current && selectedDays.length > 0) {
      setInputValue(selectedDays.join(', '));
    }
  }, [selectedDays]);

  const parseAndValidate = (value: string): number[] | null => {
    if (!value.trim()) {
      return null;
    }

    // Split by comma and parse each value
    const parts = value.split(',').map(s => s.trim()).filter(s => s !== '');
    const numbers: number[] = [];
    const seen = new Set<number>();

    for (const part of parts) {
      const num = parseInt(part, 10);
      
      // Check if it's a valid number
      if (isNaN(num)) {
        return null;
      }
      
      // Check if it's in valid range
      if (num < 1 || num > 31) {
        return null;
      }
      
      // Avoid duplicates
      if (!seen.has(num)) {
        numbers.push(num);
        seen.add(num);
      }
    }

    return numbers.length > 0 ? numbers.sort((a, b) => a - b) : null;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    isTypingRef.current = true;
    setInputValue(value);

    const parsed = parseAndValidate(value);
    if (parsed === null) {
      if (value.trim() === '') {
        setError('At least one day is required');
      } else {
        setError('Invalid input. Use numbers 1-31, separated by commas');
      }
    } else {
      setError('');
      onChange(parsed);
    }
  };

  const handleBlur = () => {
    isTypingRef.current = false;
    // Clean up the display on blur
    if (selectedDays.length > 0) {
      setInputValue(selectedDays.join(', '));
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      <Tooltip title="Note: Day 31 will appear on the last day of shorter months" arrow placement="top">
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', cursor: 'help' }}>
          Days of month (1-31)
        </Typography>
      </Tooltip>
      <TextField
        fullWidth
        size="small"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        placeholder="e.g., 1, 15, 31"
        error={!!error}
        sx={{ mb: 0.5 }}
      />
      {error && (
        <FormHelperText error>{error}</FormHelperText>
      )}
    </Box>
  );
};

export default MonthDayInput;
